/**
 * DogProfile - Strona profilu pojedynczego psa
 * Wzorowana na ReproducerPanel - z opcjami edycji, usunięcia, etc.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dog, Edit, Trash2, Eye, ExternalLink, Loader2, 
  MoreVertical, EyeOff, ArrowLeft, Heart, Calendar,
  MapPin, Award, Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';
import { TitleBadge, TrainingBadge } from '@/components/DogBadges';
import ShortLinkButton from '@/components/ShortLinkButton';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function DogProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dog, setDog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDog();
    }
  }, [id]);

  const fetchDog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Try fetching as regular dog first
      try {
        const res = await axios.get(`${API}/dogs/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDog(res.data);
        return;
      } catch (dogError) {
        // If not found as dog, try as stud
        if (dogError.response?.status === 404) {
          const studRes = await axios.get(`${API}/studs/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setDog({ ...studRes.data, listing_type: 'stud', is_stud: true });
          return;
        }
        throw dogError;
      }
    } catch (e) {
      console.error('Error fetching dog:', e);
      toast.error('Nie znaleziono psa');
      navigate('/panel');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (dog?.kennel_id) {
      navigate(`/wizard/pies/${dog.kennel_id}/${id}`);
    } else {
      navigate(`/panel/pies/${id}/edytuj`);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use correct endpoint based on dog type
      const isStud = dog?.listing_type === 'stud' || dog?.is_stud;
      const endpoint = isStud ? `${API}/studs/${id}` : `${API}/dogs/${id}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isStud ? 'Reproduktor usunięty' : 'Pies usunięty');
      navigate('/panel');
    } catch (e) {
      console.error('Delete error:', e);
      const errorMsg = e.response?.data?.detail || 'Błąd usuwania';
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleToggleVisibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const newHiddenState = !dog.is_hidden;
      
      // Use correct endpoint based on dog type
      const isStud = dog?.listing_type === 'stud' || dog?.is_stud;
      const endpoint = isStud ? `${API}/studs/${id}` : `${API}/dogs/${id}`;
      
      await axios.put(endpoint, {
        ...dog,
        is_hidden: newHiddenState
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDog(prev => ({ ...prev, is_hidden: newHiddenState }));
      toast.success(newHiddenState ? 'Ukryty' : 'Widoczny');
    } catch (e) {
      console.error('Toggle visibility error:', e);
      toast.error('Błąd zmiany widoczności');
    }
  };

  const handleViewPublic = () => {
    if (dog?.slug) {
      const isStud = dog?.listing_type === 'stud' || dog?.is_stud;
      const publicPath = isStud ? `/reproduktor/${dog.slug}` : `/pies/${dog.slug}`;
      window.open(publicPath, '_blank');
    }
  };

  // Loading state
  if (loading) {
    return (
      <PanelLayout title="Profil psa">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  // Dog not found
  if (!dog) {
    return (
      <PanelLayout title="Profil psa">
        <div className="text-center py-12">
          <Dog className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pies nie znaleziony</h2>
          <p className="text-gray-500 mb-4">Nie udało się znaleźć tego psa</p>
          <Link to="/panel">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Wróć do panelu
            </Button>
          </Link>
        </div>
      </PanelLayout>
    );
  }

  const genderSymbol = dog.gender === 'female' ? '♀' : '♂';
  const genderColor = dog.gender === 'female' ? 'text-pink-500' : 'text-blue-500';
  const isHidden = dog.is_hidden || dog.status === 'hidden';

  return (
    <PanelLayout title={dog.name} subtitle="Profil psa">
      <div className="max-w-4xl mx-auto">
        {/* Back link */}
        <Link to="/panel" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Wróć do panelu
        </Link>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
        >
          {/* Header with photo */}
          <div className="relative">
            <div className="h-48 bg-gradient-to-br from-[#0B1F3B] to-[#143A66]">
              {dog.main_photo && (
                <img 
                  src={getImageUrl(dog.main_photo)} 
                  alt={dog.name}
                  className="w-full h-full object-cover opacity-30"
                />
              )}
            </div>
            
            {/* Profile photo */}
            <div className="absolute -bottom-16 left-6">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white shadow-lg bg-white">
                {dog.main_photo ? (
                  <img 
                    src={getImageUrl(dog.main_photo)} 
                    alt={dog.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <Dog className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            {/* Actions dropdown */}
            <div className="absolute top-4 right-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="icon" className="bg-white/90 hover:bg-white">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white">
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edytuj
                  </DropdownMenuItem>
                  {dog.slug && (
                    <DropdownMenuItem onClick={handleViewPublic}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Zobacz stronę
                    </DropdownMenuItem>
                  )}
                  {dog.id && (
                    <DropdownMenuItem asChild>
                      <ShortLinkButton 
                        entityType={dog?.is_stud ? "stud" : "dog"} 
                        entityId={dog.id} 
                        entityName={dog.name}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start px-2 py-1.5 h-auto font-normal"
                      />
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleToggleVisibility}>
                    {isHidden ? (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Pokaż
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" />
                        Ukryj
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Usuń
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Content */}
          <div className="pt-20 px-6 pb-6">
            {/* Name & status */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  {dog.name}
                  <span className={`text-2xl ${genderColor}`}>{genderSymbol}</span>
                </h1>
                {dog.pedigree_name && dog.pedigree_name !== dog.name && (
                  <p className="text-amber-600 font-medium">{dog.pedigree_name}</p>
                )}
                <p className="text-gray-500">{dog.breed}</p>
              </div>
              
              <div className="flex items-center gap-2">
                {isHidden ? (
                  <Badge className="bg-gray-100 text-gray-500 border-gray-200">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Ukryty
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200">
                    Aktywny
                  </Badge>
                )}
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {dog.birth_date && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Data urodzenia
                  </div>
                  <p className="font-medium text-gray-900">{dog.birth_date}</p>
                </div>
              )}
              {dog.color && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Heart className="w-4 h-4" />
                    Umaszczenie
                  </div>
                  <p className="font-medium text-gray-900">{dog.color}</p>
                </div>
              )}
              {dog.role && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Dog className="w-4 h-4" />
                    Rola
                  </div>
                  <p className="font-medium text-gray-900">
                    {dog.role === 'breeding' ? 'Hodowlany' : dog.role === 'stud' ? 'Reproduktor' : dog.role}
                  </p>
                </div>
              )}
              {dog.stud_available && (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm mb-1">
                    <Heart className="w-4 h-4" />
                    Dostępny do krycia
                  </div>
                  <p className="font-medium text-emerald-700">{dog.stud_price || 'Tak'}</p>
                </div>
              )}
            </div>

            {/* Badges */}
            {(dog.titles?.length > 0 || dog.training?.length > 0) && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Tytuły i szkolenia</h3>
                <div className="flex flex-wrap gap-2">
                  {dog.titles?.map((title, i) => (
                    <TitleBadge key={`t-${i}`} title={title} />
                  ))}
                  {dog.training?.map((tr, i) => (
                    <TrainingBadge key={`tr-${i}`} training={tr} />
                  ))}
                </div>
              </div>
            )}

            {/* Health tests */}
            {dog.health_tests_text && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Badania zdrowotne</h3>
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Shield className="w-4 h-4" />
                    <span>{dog.health_tests_text}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {dog.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Opis</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{dog.description}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <Button onClick={handleEdit} className="bg-[#F47A3B] hover:bg-[#E07A0F] text-white">
                <Edit className="w-4 h-4 mr-2" />
                Edytuj profil
              </Button>
              {dog.slug && (
                <Button variant="outline" onClick={handleViewPublic}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Zobacz stronę publiczną
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Gallery */}
        {dog.gallery?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Galeria ({dog.gallery.length} zdjęć)</h3>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {dog.gallery.map((img, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img 
                    src={getImageUrl(img)} 
                    alt={`${dog.name} - zdjęcie ${i + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl border border-gray-200 p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-2">Usuń psa</h3>
            <p className="text-gray-500 mb-6">
              Czy na pewno chcesz usunąć <span className="text-gray-900 font-medium">{dog.name}</span>? Tej operacji nie można cofnąć.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 border-gray-200"
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Usuń
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </PanelLayout>
  );
}
